import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/core/services/auth.service';
import { UserService } from 'src/app/core/services/user.service';
import { Location } from '@angular/common';
import { AppButtonComponent } from 'src/app/shared/components/app-button/app-button.component';
import {
  AppInputCustomComponent,
  InputMaskTypes,
} from 'src/app/shared/components/app-input-custom/app-input-custom.component';
import {
  AppInputComponent,
  InputType,
} from 'src/app/shared/components/app-input/app-input.component';
import { AlertService, AlertType } from 'src/app/core/services/alert.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { ModalService } from 'src/app/core/services/modal.service';
import { AppModalComponent } from 'src/app/shared/components/app-modal/app-modal.component';
import { MemberService } from 'src/app/core/services/member.service';
import { ReplaceTextPipe } from "../../shared/pipe/replace-text.pipe";
import { LocalStorageService } from 'src/app/core/services/local-storage.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    AppButtonComponent,
    ReactiveFormsModule,
    AppInputCustomComponent,
    AppInputComponent,
    RouterLink,
    AppModalComponent,
    ReplaceTextPipe
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  returnUrl: string;
  error: string;

  checkboxLabel = ``;

  loginDependent: any = false;

  InputTypeEnum = InputType;
  InputMaskTypesEnum = InputMaskTypes;

  currentMask: InputMaskTypes = InputMaskTypes.CLEAR;

  logoUrl: string = localStorage.getItem('imageLogo') || '';
  pageTitle = 'Faça seu login';

  loginFormGroup = new FormGroup({
    loginControl: new FormControl<string>('', Validators.required),
    passwordControl: new FormControl<string>('', Validators.required),
    isDependent: new FormControl<boolean>(false)
  });

  buttonOptions = {
    buttonText: 'Voltar',
    buttonBorderWidth: '1px',
    buttonSize: 'btn btn-md px-3',
    borderRadius: '25px',
  };

  wannaBePartnerBtn = {
    buttonText: 'Quero ser sócio',
    borderRadius: '25px',
    buttonBorderWidth: '1px',
    buttonSize: 'btn btn-sm px-4 py-2 btn-primary',
  };

  loginBtn = {
    buttonText: 'Entrar',
    borderRadius: '25px',
    buttonBorderWidth: 'none',
    buttonSize: 'btn btn-sm px-5 py-2 btn-primary',
  };

  modalTitle = 'Bem-vindo!';
  modalId = 'welcomeModal';
  modalBodyMessage = 'Esta é uma mensagem de boas-vindas.';

  private _destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private userService: UserService,
    private location: Location,
    private alertService: AlertService,
    private spinnerService: SpinnerService,
    private localStorageService: LocalStorageService,
    private modalService: ModalService,
    private memberService: MemberService
  ) {
    const currentUserValue = this.userService.currentUserValue
      ? Object.keys(this.userService.currentUserValue)
      : null;
    if (
      currentUserValue?.length &&
      !this.route.snapshot.queryParams['backoffice']
    ) {
      this.router.navigate([this.userService.currentUserValue?.isDependent ? '/dashboard-dependente' : '/dashboard']);
    } else {
      this.spinnerService.hide();
    }
  }

  ngOnInit() {
    this.returnUrl = this.route.snapshot.queryParams['returnUrl'] ||
      (this.userService.currentUserValue?.isDependent ? '/dashboard-dependente' : '/dashboard');

    const backoffice = this.route.snapshot.queryParams['backoffice'];

    if (backoffice === 'true') {
      const userId = this.route.snapshot.queryParams['login'];

      // Encerra a sessão atual (storage + subjects) para o header refletir a troca de usuário
      this.authService.clearSession();

      // Garantindo que o token foi removido e que o userID está na URL
      if (userId) {
        this.loginMemberByBackoffice(userId);
      }
    } else {
      // Verificar se há uma mensagem de login no localStorage com atraso de 100ms
      setTimeout(() => {
        this.checkLoginMessage();
      }, 100);
    }

    const loginControl = this.loginFormGroup.get('loginControl');

    loginControl?.valueChanges.subscribe(value => {
      if (!value) {
        this.currentMask = InputMaskTypes.CLEAR;
        return;
      }

      const raw = value.replace(/\D/g, '');

      const isEmail = value.includes('@');

      if (!isEmail && raw.length === 11) {
        loginControl.setValue(raw);
      }

      if (raw.length === 11) {
        this.currentMask = InputMaskTypes.CPF;
      } else {
        this.currentMask = InputMaskTypes.CLEAR;
      }
    });
  }

  goToRecover() {
    this.router.navigate(['/login/recuperar-senha'], { state: { loginDependent: this.loginDependent } });
  }

  verifyIsDependent() {
    this.loginDependent = this.loginFormGroup.get('isDependent')?.value;
  }

  isLoginDependenteEnabled(): boolean {
    return JSON.parse(localStorage.getItem('enableLoginDependente') || 'false');
  }

  onLoginChange(value: any) {


    const raw = value.replace(/\D/g, '');

    if (raw.length == 11) {
      this.currentMask = InputMaskTypes.CPF;
    }
    else if (raw.length <= 9) {
      this.currentMask = InputMaskTypes.CLEAR;
    }
    else {
      this.currentMask = InputMaskTypes.CLEAR;
    }
  }

  checkLoginMessage() {
    console.log(
      'Verificando mensagem de login...',
      localStorage.getItem('hasLoginMessage')
    );
    const hasLoginMessage = localStorage.getItem('hasLoginMessage') === 'true';
    if (hasLoginMessage) {
      this.modalTitle =
        localStorage.getItem('loginMessageTitle') || 'Bem-vindo!';
      this.modalBodyMessage =
        localStorage.getItem('loginMessage') ||
        'Esta é uma mensagem de boas-vindas.';
      this.modalService.open(this.modalId);
    }
  }

  back() {
    this.location.back();
  }

  loginMemberByBackoffice(userId: string) {
    localStorage.removeItem('isSecondaryPlan');
    localStorage.removeItem('secondaryPlanDetail');
    this.authService
      .loginMemberByBackoffice(userId)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        error: (error) => {
          this.alertService.showAlert(
            AlertType.DANGER,
            error?.error?.message || 'Erro ao fazer login automático.'
          );
        },
      });
  }

  login() {
    const userCredentials = {
      username: this.loginFormGroup.get('loginControl')?.value as string,
      password: this.loginFormGroup.get('passwordControl')?.value as string,
    };

    if (this.loginFormGroup.get('isDependent')?.value) {

      const body = {
        username: userCredentials.username,
        password: userCredentials.password
      }

      this.authService.loginDependent(body).subscribe({
        next: () => { console.log('Login realizado com sucesso.'); },
        error: (error) => { }
      });
    } else {
      this.userService.canLogin(userCredentials.username).subscribe({
        next: (canLogin) => {
          if (!canLogin) {
            this.alertService.showAlert(
              AlertType.DANGER,
              'CPF está bloqueado para login.'
            );
          } else {
            // Verificar se o parâmetro "memberClienteIngressoSA" na URL é true
            const isMemberClienteIngressoSA = this.route.snapshot.queryParamMap.get('memberClienteIngressoSA') === 'true';

            if (isMemberClienteIngressoSA) {
              // Atualizar a situação do membro apenas se o parâmetro for true
              this.memberService.updateMemberClienteIngressoSA(userCredentials.username).subscribe({
                next: (updated) => {
                  if (updated) {
                    console.log('Situação do membro atualizada para "Abandono de Carrinho".');
                  } else {
                    console.log('Membro não é ClienteIngressoSA ou não foi encontrado.');
                  }

                  // Aguardar um tempo antes de chamar o login
                  setTimeout(() => {
                    this.executeLogin(userCredentials);
                  }, 500); // Atraso de 500ms
                },
                error: (error) => {
                  console.error('Erro ao atualizar a situação do membro:', error);
                  this.alertService.showAlert(
                    AlertType.DANGER,
                    'Erro ao atualizar a situação do membro.'
                  );

                  // Mesmo em caso de erro, chamar o login após o atraso
                  setTimeout(() => {
                    this.executeLogin(userCredentials);
                  }, 500); // Atraso de 500ms
                },
              });
            } else {
              // Se o parâmetro não estiver presente, chamar o login diretamente
              this.executeLogin(userCredentials);
            }
          }
        },
        error: (error) => {
          this.alertService.showAlert(
            AlertType.DANGER,
            'Erro ao verificar o CPF.'
          );
        },
      });
    }
  }

  private executeLogin(userCredentials: { username: string; password: string }) {
    this.authService
      .login(
        userCredentials.username,
        userCredentials.password,
        this.returnUrl
      )
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: () => {
          console.log('Login realizado com sucesso.');
        },
        error: (error) => {
          if (error?.error?.code === 'ME037') {
            this.router.navigate(['/login/reenviar-email-confirmacao'], {
              state: { cpf: userCredentials.username },
            });
          } else {
            this.alertService.showAlert(
              AlertType.DANGER,
              error?.error?.message || 'Erro ao fazer login.'
            );
          }
        },
      });
  }

  onModalConfirm(event: any): void {
    console.log('Modal confirmado:', event);
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
